/**
 * The album check before ordering (design "Progression animée", album-check handoff).
 *
 * Full window : on the left the adviser's photo, ringed by a ring climbing to the share of photo
 * zones filled (with a "!" badge at the end when something is missing), on the right the
 * checks passing one after the other — waiting, running (spinner), then passed (green tick) or
 * failed (an orange box listing the pages, each one a link to it). Once they are all through,
 * "Corriger mon album" (to the first page at fault) and "Commander" show.
 *
 * The checks come from BookRepository.report() : it is already known when the panel opens, the
 * sequence is staging, not a loader. prefers-reduced-motion shows the end straight away.
 *
 * desktop.common.panels.CheckBookPanel and the mobile mobile.common.popups.CheckBook
 * only differ by their stylesheet (one layout each, both from the handoff) and by where they go
 * back to : exit() and goToPage().
 */
fx.ns("shared.panels", class AlbumCheckPanel extends fx.components.PopupPanel {

    constructor(data)
    {
        super(data);

        this.destroyOnSwitch = true;

        this.mode = fx.get("previewMode") ? "navigation" : "popup";
        this.showEffect = "fade";

        this._timers = [];
        this._onKey = this._onKey.bind(this);
    }

    // anything worth stopping the order for
    static hasIssues(report)
    {
        let pages = report.pages || [];
        for (let i = 0; i < pages.length; i++)
            if (pages[i].empty || pages[i].components.length > 0)
                return true;

        return !report.isMultipleOf4;
    }

    // ms each check takes
    get STEP_MS() { return 800; }

    get RING_RADIUS() { return 88; }

    get RING_CIRC() { return 2 * Math.PI * this.RING_RADIUS; }

    get _reducedMotion()
    {
        return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    // no PopupPanel cross nor back button : the design has its own "Fermer"
    create() {}

    // render() runs before create() : built on first use
    get _steps()
    {
        if (!this.__steps)
            this.__steps = this._buildSteps(this.data.report());

        return this.__steps;
    }

    onReady()
    {
        super.onReady();

        this._ring = this.node.querySelector(".fx-check__ring-value");
        this._percentEl = this.node.querySelector(".fx-check__percent-value");
        this._titleEl = this.node.querySelector(".fx-check__title");
        this._headingEl = this.node.querySelector(".fx-check__heading");
        this._leadEl = this.node.querySelector(".fx-check__lead");
        this._liveEl = this.node.querySelector(".fx-check__sr");
        this._actionsEl = this.node.querySelector(".fx-check__actions");
        this._stepEls = this.node.querySelectorAll(".fx-check__step");

        this.on("click", this._onClick);   // a tap on the touch builds
        this.on("ESCAPE", this.close, fx.keyboard);
        this.node.addEventListener("keydown", this._onKey);

        this._lastFocus = document.activeElement;
        this.node.querySelector(".fx-check__close").focus();

        this._raf = requestAnimationFrame(this._run.bind(this));
    }

    destroy()
    {
        // called twice on leaving (view switch, then hideDestroy's effect), the 2nd maybe after fx.Disposable.free() emptied it
        if (this.destroyed || !this._timers)
            return;

        this._stop();
        this.node && this.node.removeEventListener("keydown", this._onKey);
        super.destroy();
    }

    close()
    {
        this._leave();
        super.close();
        this.exit();
    }

    // back to the editor, overridden per platform
    exit()
    {
        this.data.event("show-editor");
    }

    // back to the editor on that page (a page id, null for none), overridden per platform
    goToPage(id)
    {
        this.data.event("show-editor");

        if (id)
            this.data.creation.event("select-page", id);
    }

    // ----------------------- //

    /**
     * One step per check, `issue` set when it found something. The page count and empty pages
     * only show when they fail : there is nothing to tick about them otherwise.
     */
    _buildSteps(report)
    {
        let zones = [], empty = [], emptyZones = 0;

        // the cover counts with the other photo zones, listed first
        for (let i = 0; i < report.pages.length; i++)
        {
            let r = report.pages[i];

            if (!r || !r.page)
                continue;

            if (r.empty && !r.page.isCover)
                empty.push(this._pageLink(r.page));
            else
            if (r.empty || r.components.length > 0)
            {
                zones.push(this._pageLink(r.page));
                emptyZones += r.empty ? 1 : r.components.length;
            }
        }

        let steps = [];

        steps.push({ label : fx.text("Vérification des pages"),
                     issue : zones.length ? this._issue("empty_photo", emptyZones, zones) : null });

        if (empty.length)
            steps.push({ label : fx.text("Pages remplies"), issue : this._issue("empty_page", empty.length, empty) });

        if (!report.isMultipleOf4)
            steps.push({ label : fx.text("Nombre de pages"), issue : this._issue("page_count", 1, []) });

        return steps;
    }

    _pageLink(page)
    {
        return { id : page.id, label : page.isCover ? fx.text("Couverture") : fx.text("p. $", page.label(undefined, "-")) };
    }

    _issue(type, count, pages)
    {
        let copy = {
            empty_photo    : [ fx.text("1 zone photo vide"), fx.text("$ zones photo vides", count), fx.text("imprimées en blanc si vous commandez") ],
            empty_page     : [ fx.text("1 page vide"), fx.text("$ pages vides", count), fx.text("imprimées en blanc si vous commandez") ],
            page_count     : [ fx.text("Une feuille sera ajoutée à la fin"), "", fx.text("le nombre de pages n'est pas un multiple de 4") ],
        }[type];

        return { type : type, title : count > 1 ? copy[1] : copy[0], hint : copy[2], pages : pages };
    }

    get _issues()
    {
        return this._steps.filter(s => s.issue);
    }

    // the first page at fault, for "Corriger mon album"
    get _firstPage()
    {
        let issue = this._issues.find(s => s.issue.pages.length);
        return issue ? issue.issue.pages[0].id : null;
    }

    get _target()
    {
        let zones = this.data.report().zones || { total : 0, filled : 0 };

        if (!zones.total)
            return this._issues.length ? 0 : 100;

        return Math.floor(zones.filled / zones.total * 100);   // never 100 % while something is missing
    }

    _headline(percent)
    {
        if (!this._issues.length)
            return fx.text("Tout est parfait, votre album est prêt !");

        if (percent >= 90)
            return fx.text("Vous y êtes presque !");

        if (percent >= 60)
            return fx.text("Encore un petit effort !");

        return fx.text("Votre album prend forme");
    }

    // ----------------------- //

    _run()
    {
        this._raf = null;

        if (this._reducedMotion || !this._steps.length)
            return this._finish();

        let steps = this._steps, target = this._target, duration = this.STEP_MS * steps.length;

        this._setStep(0, "pending");

        steps.forEach((s, i) => {
            this._timers.push(setTimeout(() => {
                this._setStep(i, s.issue ? "issue" : "ok");

                if (i + 1 < steps.length)
                    this._setStep(i + 1, "pending");
                else
                    this._finish();
            }, this.STEP_MS * (i + 1)));
        });

        let t0 = performance.now();
        let tick = (now) => {
            let k = Math.min(1, (now - t0) / duration);
            this._setPercent(Math.round(target * (1 - Math.pow(1 - k, 3))));   // ease-out cubic
            this._raf = k < 1 ? requestAnimationFrame(tick) : null;
        };
        tick(t0);
    }

    _finish()
    {
        this._stop();

        let target = this._target;

        this._setPercent(target);
        this._steps.forEach((s, i) => this._setStep(i, s.issue ? "issue" : "ok", true));

        this._titleEl.textContent = this._headline(target);
        this._leadEl.hidden = true;
        this._headingEl.classList.add("is-shown");
        this.node.querySelector(".fx-check__badge").hidden = !this._issues.length;

        let fix = this.node.querySelector(".fx-check__fix");
        fix.hidden = !this._issues.length || !this._firstPage;
        this._actionsEl.hidden = false;

        (fix.hidden ? this.node.querySelector(".fx-check__order") : fix).focus();
    }

    _stop()
    {
        this._timers.forEach(clearTimeout);
        this._timers = [];

        if (this._raf)
            cancelAnimationFrame(this._raf);
        this._raf = null;
    }

    _setPercent(p)
    {
        this._percentEl.textContent = p;
        this._ring.setAttribute("stroke-dasharray", (this.RING_CIRC * p / 100).toFixed(1) + " " + this.RING_CIRC.toFixed(1));
        this._ring.style.visibility = p > 0 ? "" : "hidden";   // the round cap would draw a dot at 0
    }

    _setStep(i, state, silent)
    {
        let el = this._stepEls[i];

        if (!el || el.classList.contains("is-" + state))
            return;

        el.className = "fx-check__step is-" + state;

        if (!silent && state == "ok")
            this._liveEl.textContent = fx.text("$ : validé", this._steps[i].label);

        if (!silent && state == "issue")
            this._liveEl.textContent = fx.text("$ : problème détecté", this._steps[i].label);
    }

    // ----------------------- //

    _onClick(e)
    {
        let target = e.target || (e.srcEvent && e.srcEvent.target),
            button = target && target.closest && target.closest("button");

        if (!button || !this.node.contains(button))
            return;

        let action = button.getAttribute("data-action"),
            page = button.getAttribute("data-page");

        if (action == "close")
            this.close();
        else
        if (action == "order")
            this._onContinue();
        else
        if (action == "fix")
            this._goToPage(this._firstPage);
        else
        if (page)
            this._goToPage(page);
    }

    _onContinue()
    {
        this._leave();
        this.hideEffect = "hslidein";
        this.data.event("check-creation-confirmed");
    }

    _goToPage(id)
    {
        this._leave();
        this.hideDestroy();
        this.goToPage(id);
    }

    // timers stopped and focus handed back, whichever way the panel goes
    _leave()
    {
        this._stop();

        if (this._lastFocus && this._lastFocus.focus && document.contains(this._lastFocus))
            this._lastFocus.focus();

        this._lastFocus = null;
    }

    // Tab stays inside the panel
    _onKey(e)
    {
        if (e.key !== "Tab")
            return;

        let buttons = Array.prototype.filter.call(this.node.querySelectorAll("button"), b => b.offsetParent !== null);

        if (!buttons.length)
            return;

        let first = buttons[0], last = buttons[buttons.length - 1];

        if (e.shiftKey && document.activeElement === first)
        {
            e.preventDefault();
            last.focus();
        }
        else
        if (!e.shiftKey && document.activeElement === last)
        {
            e.preventDefault();
            first.focus();
        }
    }

    // ----------------------- //

    render()
    {
        let esc = AlbumCheckPanel.escape,
            circ = this.RING_CIRC.toFixed(1),
            asset = fx.get("assets").personAdvice,
            avatar = asset instanceof fx.Asset ? asset.content : asset,
            ok = '<svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12l5 5 9-10"/></svg>',
            warn = '<svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><path d="M12 7v6M12 17v.5"/></svg>',
            cross = '<svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M5 5l14 14M19 5L5 19"/></svg>';

        let issueHtml = (issue) => {
            let pages = issue.pages.map(p =>
                '<li><button type="button" class="fx-check__page" data-page="' + esc(p.id) + '">' + esc(p.label) + '</button></li>'
            ).join("");

            return  '<div class="fx-check__issue">' +
                        '<div class="fx-check__issue-head">' +
                            '<span class="fx-check__issue-icon">' + warn + '</span>' +
                            '<span class="fx-check__issue-title">' + esc(issue.title) + '</span>' +
                            (issue.hint ? '<span class="fx-check__issue-hint">' + esc(issue.hint) + '</span>' : '') +
                        '</div>' +
                        (pages ? '<ul class="fx-check__pages" aria-label="' + esc(fx.text("Pages concernées")) + '">' + pages + '</ul>' : '') +
                    '</div>';
        };

        let steps = this._steps.map((s, i) =>
            '<li class="fx-check__step is-waiting" data-step="' + i + '">' +
                '<div class="fx-check__step-row">' +
                    '<span class="fx-check__step-icon" aria-hidden="true">' + ok + '</span>' +
                    '<span class="fx-check__step-label">' + esc(s.label) + '</span>' +
                '</div>' +
                (s.issue ? '<div class="fx-check__step-issue">' + issueHtml(s.issue) + '</div>' : '') +
            '</li>'
        ).join("");

        return  '<div class="fx-check" role="dialog" aria-modal="true" aria-labelledby="fx-check-title">' +
                    '<button type="button" class="fx-check__close" data-action="close" data-testid="checkbook-cancel">' + esc(fx.text("Fermer")) + '<span class="fx-check__close-icon">' + cross + '</span></button>' +
                    '<div class="fx-check__card">' +
                        '<section class="fx-check__summary">' +
                            // the adviser's photo inside the ring (design "flexilivre-verification")
                            '<div class="fx-check__ring">' +
                                '<img class="fx-check__avatar" src="' + esc(avatar) + '" alt="' + esc(fx.text("Votre conseillère Flexilivre")) + '" draggable="false">' +
                                '<svg aria-hidden="true" width="200" height="200" viewBox="0 0 200 200">' +
                                    '<circle class="fx-check__ring-track" cx="100" cy="100" r="' + this.RING_RADIUS + '" fill="none" stroke-width="12"/>' +
                                    '<circle class="fx-check__ring-value" cx="100" cy="100" r="' + this.RING_RADIUS + '" fill="none" stroke-width="12" stroke-linecap="round" stroke-dasharray="0 ' + circ + '" transform="rotate(-90 100 100)"/>' +
                                '</svg>' +
                                '<span class="fx-check__badge" hidden>' + warn + '</span>' +
                            '</div>' +
                            '<div class="fx-check__percent-row">' +
                                '<span class="fx-check__percent"><span class="fx-check__percent-value">0</span>&nbsp;%</span>' +
                                '<span class="fx-check__percent-caption">' + esc(fx.text("complété")) + '</span>' +
                            '</div>' +
                            '<h1 class="fx-check__title" id="fx-check-title">' + esc(fx.text("Vérification en cours…")) + '</h1>' +
                            '<p class="fx-check__lead">' + esc(fx.text("Nous passons votre album en revue avant l'impression.")) + '</p>' +
                        '</section>' +
                        '<section class="fx-check__details">' +
                            // in place from the start, only shown at the end : the steps do not move when it appears
                            '<h2 class="fx-check__heading">' + esc(fx.text("Votre album a été vérifié")) + '</h2>' +
                            '<ol class="fx-check__steps">' + steps + '</ol>' +
                            '<p class="fx-check__sr" aria-live="polite"></p>' +
                            '<div class="fx-check__actions" hidden>' +
                                '<button type="button" class="fx-check__fix" data-action="fix" data-testid="checkbook-fix">' + esc(fx.text("Corriger mon album")) + '</button>' +
                                '<button type="button" class="fx-check__order" data-action="order" data-testid="checkbook-order">' + esc(fx.text("Commander")) + '</button>' +
                            '</div>' +
                        '</section>' +
                    '</div>' +
                '</div>';
    }

    static escape(s)
    {
        return String(s).replace(/[&<>"']/g, c => ({ "&" : "&amp;", "<" : "&lt;", ">" : "&gt;", '"' : "&quot;", "'" : "&#39;" })[c]);
    }

});
